import React, { useRef } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  UploadFile,
  PictureAsPdf,
  Description,
  Image,
  InsertDriveFile,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import theme from "../../utils/theme";
import IconButtons from "../IconButtons";
import AppButton from "../AppButton";

const styles = {
  visuallyHiddenInput: {
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  },
  errorText: {
    color: "error.main",
    mb: 2,
  },
  listContainer: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "4px",
    bgcolor: "background.paper",
  },
  emptyState: {
    justifyContent: "center",
    py: 6,
  },
  listItem: (idx, safeFiles) => ({
    borderBottom: idx < safeFiles.length - 1 ? "1px solid" : "none",
    borderColor: "divider",
    "&:last-child": { borderBottom: "none" },
  }),
  indexNumber: {
    minWidth: "24px",
    color: "text.secondary",
    fontWeight: 500,
    mr: 1,
  },
  fileNameContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap",
  },
  progressContainer: {
    width: "100%",
    mt: 1,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    ml: "auto",
  },
  fileInfoText: {
    color: "text.secondary",
    display: "block",
    mt: 1,
    textAlign: "left",
  },
  gridItem: {
    alignItems: "center",
    background: theme.palette.background.default,
    borderRadius: 2,
  },
  // New styles added for consistency
  headerContainer: {
    mb: 2,
  },
  emptyStateText: {
    color: "text.secondary",
  },
  fileName: {
    fontWeight: 500,
  },
  fileDetails: {
    color: "text.secondary",
    mt: 0.5,
  },
  fileCountText: {
    color: "text.secondary",
  },
};

const VisuallyHiddenInput = styled("input")(styles.visuallyHiddenInput);

const getFileIcon = (fileType, fileName) => {
  const fileExtension = fileName?.split(".").pop()?.toLowerCase();

  if (
    fileType?.includes("image/") ||
    ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(fileExtension)
  )
    return <Image sx={{ color: theme.palette.success.main, mr: 1 }} />;
  if (fileType === "application/pdf" || fileExtension === "pdf")
    return <PictureAsPdf sx={{ color: theme.palette.error.dark, mr: 1 }} />;
  if (
    fileType?.includes("text/") ||
    ["txt", "csv", "json", "xml"].includes(fileExtension)
  )
    return <Description sx={{ color: theme.palette.info.main, mr: 1 }} />;
  if (
    fileType?.includes("word") ||
    fileType?.includes("document") ||
    ["doc", "docx"].includes(fileExtension)
  )
    return <Description sx={{ color: theme.palette.primary.main, mr: 1 }} />;
  if (["xls", "xlsx"].includes(fileExtension))
    return <Description sx={{ color: theme.palette.success.dark, mr: 1 }} />;
  return (
    <InsertDriveFile sx={{ color: theme.palette.text.secondary, mr: 1 }} />
  );
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "Unknown size";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

// Helper function to extract filename from URL
const getFileNameFromUrl = (url) => {
  if (!url) return "Unknown file";
  return url.split("/").pop() || "Unknown file";
};

// Check if file is a File object (new upload)
const isFileObject = (file) =>
  file instanceof File || (file && file.name && file.size);

// Check if file is a document object (existing file from server)
const isDocumentObject = (file) => file && file.document && !file.name;

const FileUploaderUI = ({
  files = [],
  onFilesSelect,
  onFileDelete,
  viewOnly = false,
  title = "Upload Documents",
  acceptedFileTypes,
  error,
  touched,
  inputProps,
}) => {
  const safeFiles = Array.isArray(files) ? files : [];
  const fileInputRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const handleFileButtonClick = () => {
    // Save current scroll position
    scrollPositionRef.current = window.scrollY;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      // Prevent default behavior that causes scrolling
      event.preventDefault();
      event.stopPropagation();

      const files = Array.from(event.target.files);

      onFilesSelect(files);

      // Reset the input
      event.target.value = "";

      // Restore scroll position after a microtask
      setTimeout(() => {
        window.scrollTo(0, scrollPositionRef.current);
      }, 0);
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={styles.headerContainer}
      >
        {!viewOnly && (
          <AppButton
            label={title}
            buttonType="main"
            onClick={handleFileButtonClick}
            endIcon={<UploadFile />}
          />
        )}

        <VisuallyHiddenInput
          ref={fileInputRef}
          id={inputProps?.id || "file-input"}
          type="file"
          multiple
          accept={acceptedFileTypes.join(",")}
          onChange={handleFileInputChange}
          disabled={viewOnly}
          {...inputProps}
        />

        {safeFiles.length > 0 && !viewOnly ? (
          <Typography variant="body2" sx={styles.fileCountText}>
            {safeFiles.length}{" "}
            {safeFiles.length === 1 ? "document" : "documents"} uploaded
          </Typography>
        ) : null}
      </Stack>

      {error && touched && (
        <Typography variant="body2" sx={styles.errorText}>
          {error}
        </Typography>
      )}

      <List dense sx={styles.listContainer}>
        {safeFiles.length === 0 ? (
          <ListItem sx={styles.emptyState}>
            <Typography variant="body1" sx={styles.emptyStateText}>
              No documents uploaded
            </Typography>
          </ListItem>
        ) : (
          <Grid container spacing={2} p={2}>
            {safeFiles.map((file, idx) => {
              const isExistingFile = isDocumentObject(file);
              const isNewFile = isFileObject(file);

              const fileName = isNewFile
                ? file.name
                : getFileNameFromUrl(file.document);
              const fileType = isNewFile
                ? file.type
                : file.document
                ? "application/octet-stream"
                : "";
              const fileSize = isNewFile ? file.size : null;
              const uploadDate = isExistingFile ? file.created_at : null;

              return (
                <Grid
                  size={{ xs: 12, lg: 6 }}
                  key={
                    isExistingFile
                      ? `existing-${file.document_id || idx}`
                      : `new-${file.name || idx}`
                  }
                  sx={styles.gridItem}
                >
                  <ListItem sx={styles.listItem(idx, safeFiles)}>
                    <Typography variant="body2" sx={styles.indexNumber}>
                      {idx + 1}.
                    </Typography>

                    {getFileIcon(fileType, fileName)}

                    <ListItemText
                      primary={
                        <Box component="span" sx={styles.fileNameContainer}>
                          <Typography variant="body2" sx={styles.fileName}>
                            {fileName.slice(0, 25)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={styles.progressContainer}>
                          <Typography variant="caption" sx={styles.fileDetails}>
                            {isNewFile && fileSize
                              ? formatFileSize(fileSize)
                              : ""}
                            {isExistingFile && uploadDate
                              ? `Uploaded on ${new Date(
                                  uploadDate
                                ).toLocaleDateString()}`
                              : ""}
                            {!isNewFile && !isExistingFile
                              ? "Unknown file"
                              : ""}
                          </Typography>
                        </Box>
                      }
                    />

                    <Box sx={styles.actionContainer}>
                      {!viewOnly && (
                        <Tooltip title="Remove document" arrow placement="top">
                          <IconButtons
                            size="small"
                            aria-label="Delete document"
                            onClick={() => onFileDelete(file)}
                            buttonType="delete"
                          />
                        </Tooltip>
                      )}

                      {(isExistingFile || isNewFile) && (
                        <Box
                          component="a"
                          href={
                            isExistingFile
                              ? file.document
                              : file.document || URL.createObjectURL(file)
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconButtons
                            size="small"
                            aria-label="Delete document"
                            onClick={() => {}}
                            buttonType="view"
                          />
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                </Grid>
              );
            })}
          </Grid>
        )}
      </List>

      {safeFiles.length > 0 && (
        <Typography variant="caption" sx={styles.fileInfoText}>
          Supported formats: {acceptedFileTypes.join(", ")}
        </Typography>
      )}
    </Box>
  );
};

export default FileUploaderUI;
